import { modalActions } from "@context/modals";
import classNames from "classnames";
import { MODAL_TYPES } from "types/modals";

import { toFarsiNumber } from "@utils/digits";
import { twMerge } from "tailwind-merge";
import { SVGProps, useState } from "react";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import { ArrowLeftIcon } from "@heroicons/react/solid";
import { COURSE_TYPE, LESSON_STEP_TYPE, StepPreviewItem } from "@types";
import VideoIcon from "@svg/new-design/video";
import DocumentPaper3LinesIcon from "@svg/new-design/note";
import BoardTaskIcon from "@svg/new-design/exam";
import Document2LineEditIcon from "@svg/new-design/document-2line-edit";
import VoiceIcon from "@svg/new-design/voice";
import { TextractorIcon } from "@modules/course/access/side-bar/lesson-details/Icon";
import { useLayoutContext } from "@context/layout";
import { useAccess } from "@templates/course/hooks/useAccess";

export const textLessonTypes = [
  LESSON_STEP_TYPE.SUMMARY_NOTE,
  LESSON_STEP_TYPE.GLOSSARY,
  LESSON_STEP_TYPE.INTERVIEW_QUESTIONS,
  LESSON_STEP_TYPE.FAQ,
  LESSON_STEP_TYPE.RELATED_JOB,
  LESSON_STEP_TYPE.NOTE,
];

interface Props {
  openForGuest: boolean;
  title: string;
  subtitle: string | null;
  stepIndex: number;
  allSteps: StepPreviewItem | undefined;
  color?: string;
  iconComponent: React.FC<SVGProps<SVGSVGElement>>;
  duration: string;
  message?: string | React.ReactNode;
  isFree: boolean;
  id: number | undefined;
  courseType: LESSON_STEP_TYPE;
  customIcon?: React.ReactNode;
}
const HeadlineItem: React.FC<Props> = ({
  openForGuest,
  title,
  subtitle,
  stepIndex,
  allSteps,
  color,
  iconComponent: IconComponent,
  duration,
  message,
  isFree,
  id,
  courseType,
  customIcon,
}) => {
  const layoutData = useLayoutContext();
  const isMobile = useIsMobile(640);
  const { courseData } = useAccess();

  const isLogin = layoutData?.userData?.is_login;
  const showModal = openForGuest || (isFree && isLogin);

  const [durationContent, setDurationContent] = useState<
    string | React.ReactNode
  >(toFarsiNumber(duration));

  const onMouseEnter = () => {
    if (message) setDurationContent(message);
  };

  const onMouseLeave = () => {
    setDurationContent(toFarsiNumber(duration));
  };

  const showModalPreview = () => {
    if (isMobile) {
      modalActions.addModal(MODAL_TYPES.MOBILE_PLAYER_VIDEO, {
        data: {
          productId: courseData.product?.id,
          lessonId: id || null,
          showHeader: false,
          subTitle: subtitle,
          allSteps,
          stepIndex,
          id: id,
        },
        config: {
          hideCloseIcon: true,
          modalClassName: "overflow-scroll",
          closeIcon: isMobile
            ? (closeModal) => (
                <ArrowLeftIcon
                  className="cursor-pointer w-7   h-7"
                  onClick={closeModal}
                />
              )
            : undefined,
        },
      });
    } else {
      modalActions.addModal(MODAL_TYPES.VIDEO_PLAYER, {
        data: {
          productId: courseData.product?.id,
          lessonId: id || null,
          showHeader: false,
          subTitle: subtitle,
          allSteps,
          stepIndex,
          id: id,
        },
        config: {
          hideCloseIcon: true,

          closeIcon: isMobile
            ? (closeModal) => (
                <ArrowLeftIcon
                  className="cursor-pointer w-7   h-7"
                  onClick={closeModal}
                />
              )
            : undefined,
          modalClassName: "max-w-[800px]",
          modalHeaderClassName:
            "flex justify-between items-center px-6 pt-6 pb-1.5 text-white",
          modalContentClassName:
            "h-[calc(100%-64px)] flex flex-col justify-center md:block max-h-[calc(100vh-64px)] overflow-y-auto",
        },
      });
    }
  };

  const iconClassNames =
    "ml-2 grow-0 shrink-0 w-4 md:w-5 h-4 md:h-5 text-fds-icon-disable";
  const ItemIconComponent = {
    [LESSON_STEP_TYPE.NOTE]: (
      <DocumentPaper3LinesIcon className={iconClassNames} />
    ),
    [LESSON_STEP_TYPE.EXAM]: <BoardTaskIcon className={iconClassNames} />,
    [LESSON_STEP_TYPE.PRACTICE]: (
      <Document2LineEditIcon className={iconClassNames} />
    ),
    [COURSE_TYPE.PODCAST]: <VoiceIcon className={iconClassNames} />,
    default: (
      <VideoIcon
        className={classNames(
          iconClassNames,
          isFree ? "text-fds-icon-secondary" : "text-fds-icon-disable"
        )}
      />
    ),
  };
  return (
    <>
      <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={classNames(
          { "cursor-pointer": showModal },
          "sm:px-6  px-4     hover:bg-fds-canvas-primary justify-between  w-full py-2.5   flex items-center"
        )}
        onClick={showModal ? showModalPreview : undefined}
      >
        <div className="flex items-center w-[calc(100%-90px)] ">
          {customIcon ||
            ItemIconComponent[
              (courseType as
                | LESSON_STEP_TYPE.PRACTICE
                | LESSON_STEP_TYPE.EXAM
                | LESSON_STEP_TYPE.NOTE) || "default "
            ] ||
            TextractorIcon(
              courseType,
              classNames(
                "ml-2 grow-0 shrink-0 w-4 h-4 md:w-5 md:h-5",
                isFree ? "text-fds-icon-secondary" : "text-fds-icon-disable"
              )
            ) || (
              <VideoIcon
                className={classNames(
                  "ml-2 grow-0 shrink-0 w-4 h-4 md:w-5 md:h-5",
                  isFree ? "text-fds-icon-secondary" : "text-fds-icon-disable"
                )}
              />
            )}
          <p
            className={classNames(
              color,
              "body-sm md:body-md text-ellipsis whitespace-nowrap overflow-hidden ml-2 ",
              isFree ? "text-fds-primary" : "text-fds-secondary"
            )}
          >
            {toFarsiNumber(title)}
          </p>
        </div>
        <div className="flex grow shrink-0 justify-end items-center">
          <span className={classNames(color, "body-md text-fds-tertiary ")}>
            {textLessonTypes.includes(courseType)
              ? "درس متنی"
              : courseType === LESSON_STEP_TYPE.EXAM
                ? ""
                : courseType === LESSON_STEP_TYPE.PRACTICE
                  ? "تمرین"
                  : durationContent}
          </span>
          <IconComponent
            className={twMerge(
              color,
              "mr-3  grow-0 shrink-0 w-5 h-5 md:w-6 md:h-6 ",
              showModal ? "text-fds-icon-brand" : "text-fds-icon-disable"
            )}
          />
        </div>
      </div>
    </>
  );
};

export default HeadlineItem;
